import { useVendorFlags } from "../lib/useVendorFlags";
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { fetchCategories, submitTool } from "../lib/api";
import { PRICING_OPTIONS, PRICING_LABELS } from "../components/PricingLegend";

export default function Submit() {
  const { flags: vendorFlags } = useVendorFlags();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [assetClasses, setAssetClasses] = useState([]);
  const [instruments, setInstruments] = useState([]);
  const [regions, setRegions] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [deliveryOptions, setDeliveryOptions] = useState([]);
  const [deliveryGroups, setDeliveryGroups] = useState([]);
  const [deliveryLabels, setDeliveryLabels] = useState({});
  const [proSegments, setProSegments] = useState([]);
  const [pricingModels, setPricingModels] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    title: "",
    url: "",
    prompt_text: "",
    asset_class: "Equities",
    category: "",
    pricing: "$?",
    pricing_model: "",
    description: "",
    key_feature: "",
    is_owner: false,
    is_open_source: false,
    submitter_name: "",
    submitter_email: "",
    category_2: "",
    audience: "",
    pro_segment: "",
    builder: "",
    // Multi-selects stored as Set in state; serialized to comma string on
    // submit. Backend accepts either string or list and normalizes.
    instruments: new Set(),
    regions: new Set(),
    sectors: new Set(),
    delivery: new Set(),
  });

  useEffect(() => {
    fetchCategories().then((r) => {
      setCategories(r.categories || []);
      setAssetClasses(r.asset_classes || []);
      setInstruments(r.instruments || []);
      setRegions(r.regions || []);
      setSectors(r.sectors || []);
      setDeliveryOptions(r.delivery || []);
      setDeliveryGroups(r.delivery_groups || []);
      setDeliveryLabels(r.delivery_labels || {});
      setProSegments(r.pro_segments || []);
      setPricingModels(r.pricing_models || []);
      // Honor ?delivery=<a,b,c> from the SubmitDropdown — pre-check every
      // surface the submitter ticked (multi-select).
      const preset = searchParams.get("delivery");
      if (preset) {
        const valid = (r.delivery || []);
        const keys = preset.split(",").map((s) => s.trim()).filter((k) => valid.includes(k));
        if (keys.length) {
          setForm((f) => ({ ...f, delivery: new Set(keys) }));
        }
      }
      // ?os=1 from the "open source" dropdown entry pre-checks the flag.
      if (searchParams.get("os") === "1") {
        setForm((f) => ({ ...f, is_open_source: true }));
      }
      // Prefill from the ⌘K palette "add to index" CTA (?title=…&url=…).
      const presetTitle = searchParams.get("title");
      const presetUrl = searchParams.get("url");
      if (presetTitle || presetUrl) {
        setForm((f) => ({
          ...f,
          ...(presetTitle ? { title: presetTitle } : {}),
          ...(presetUrl ? { url: presetUrl } : {}),
        }));
      }
      setForm((f) => ({ ...f, category: (r.categories || [])[0] || "" }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const toggleSet = (k, v) => () =>
    setForm((f) => {
      const next = new Set(f[k]);
      if (next.has(v)) next.delete(v); else next.add(v);
      return { ...f, [k]: next };
    });

  const onSubmit = async (e) => {
    e.preventDefault();
    const isPrompt = form.delivery.has("prompt");
    if (!form.title || !form.description || !form.key_feature || !form.category || !form.asset_class) {
      toast.error("Please fill all required fields");
      return;
    }
    if (isPrompt && !form.prompt_text.trim() && !form.url.trim()) {
      toast.error("paste the prompt body (or add a URL where it lives)");
      return;
    }
    if (!isPrompt && !form.url) {
      toast.error("paste the tool's URL (or check `prompt` under Delivery to host one here)");
      return;
    }
    if (form.description.length > 240) {
      toast.error("Description must be <= 240 chars");
      return;
    }
    setSubmitting(true);
    try {
      await submitTool({
        title: form.title.trim(),
        url: form.url.trim() || null,
        prompt_text: form.prompt_text.trim() || null,
        asset_class: form.asset_class,
        category: form.category,
        pricing: isPrompt && !form.url.trim() ? "FREE" : form.pricing,
        pricing_model: form.pricing_model || null,
        description: form.description.trim(),
        key_feature: form.key_feature.trim(),
        is_owner: form.is_owner,
        submitter_name: form.submitter_name.trim() || null,
        submitter_email: form.submitter_email.trim() || null,
        category_2: form.category_2 || null,
        audience: form.audience || null,
        pro_segment: form.audience === "pro" ? (form.pro_segment || null) : null,
        builder: form.builder || null,
        instruments: Array.from(form.instruments),
        regions: Array.from(form.regions),
        sectors: Array.from(form.sectors),
        delivery: Array.from(form.delivery),
        is_open_source: form.is_open_source,
      });
      setDone(true);
      toast.success("Submitted. A human will review it.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="submit-success-page">
        <Header />
        <main className="max-w-3xl mx-auto px-4 py-10">
          <div className="tv-card" data-testid="submit-success">
            <h1 className="text-xl font-bold">{">"} Submitted.</h1>
            <p className="text-[13px] mt-2 text-[#444]">
              A human will review your submission and approve it if it&apos;s a fit. Spam and scams are removed on sight.
            </p>
            <div className="mt-4 flex gap-2">
              <Link to="/" className="tv-btn" data-testid="back-home-btn">[<span className="text-black">←</span>] back to index</Link>
              <button className="tv-btn" onClick={() => { setDone(false); setForm({ title: "", url: "", asset_class: "Equities", category: categories[0] || "", pricing: "$?", pricing_model: "", description: "", key_feature: "", is_owner: false, is_open_source: false, submitter_name: "", submitter_email: "", category_2: "", audience: "", pro_segment: "", builder: "", instruments: new Set(), regions: new Set(), sectors: new Set(), delivery: new Set() }); }} data-testid="submit-another-btn">
                [+] submit another
              </button>
            </div>
          </div>

          {vendorFlags.starter && form.is_owner && (
            <div className="tv-card mt-4 border-black" data-testid="submit-starter-upsell">
              <h2 className="text-[14px] font-bold">{">"} Built this yourself?</h2>
              <p className="text-[13px] mt-2 text-[#444]">
                Once it&apos;s approved, claim the listing free with your work email. Then upgrade to
                Starter to own the page — your own call-to-action buttons, a built-in &quot;request a demo&quot;
                form, screenshots, a short pitch in your words, and rank badges for your site.
              </p>
              <div className="mt-3">
                <Link to="/for-vendors" className="tv-btn tv-btn-primary" data-testid="submit-starter-cta">
                  see Starter →
                </Link>
              </div>
            </div>
          )}
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div data-testid="submit-page">
      <Seo
        title="Submit a tool"
        description="Submit an investing tool to the TerminalValue.io index. Manual human review."
        path="/submit"
      />
      <Header />
      <main className="max-w-3xl mx-auto px-4 py-8">
        <h1 className="text-xl font-bold border-b border-black pb-2">{">"}_ Submit a Tool</h1>
        <p className="text-[13px] text-[#444] mt-2">
          One-line proof of utility. Manual review (typically &lt; 48h).
        </p>

        <form onSubmit={onSubmit} className="mt-6 grid grid-cols-1 gap-4" data-testid="submit-form">
          <Field label="Title *" testid="field-title">
            <input type="text" required maxLength={120} className="tv-input" value={form.title} onChange={update("title")} placeholder="e.g. Stock Unlock" data-testid="input-title" />
          </Field>

          <Field label={form.delivery.has("prompt") ? "URL (optional — only if the prompt is also documented somewhere)" : "URL *"} testid="field-url">
            <input type="text" maxLength={500} className="tv-input" value={form.url} onChange={update("url")} placeholder="https://your-tool.com" data-testid="input-url" />
          </Field>

          {form.delivery.has("prompt") && (
            <Field label="Prompt body *" testid="field-prompt-text" hint={`${form.prompt_text.length}/8000`}>
              <textarea
                required
                maxLength={8000}
                rows={10}
                className="tv-textarea font-mono text-[12px]"
                value={form.prompt_text}
                onChange={update("prompt_text")}
                placeholder="paste the full prompt body here. preserve formatting — readers will copy this verbatim into claude/chatgpt/gemini."
                data-testid="input-prompt-text"
              />
            </Field>
          )}

          <Field label="Asset class *" testid="field-asset">
            <select required className="tv-select" value={form.asset_class} onChange={update("asset_class")} data-testid="input-asset-class">
              {assetClasses.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </Field>

          <Field label="Delivery — what did you build?" testid="field-delivery" hint="pick all that apply">
            <div className="space-y-3" data-testid="input-delivery">
              {(deliveryGroups.length > 0
                ? deliveryGroups
                : [{ key: "all", label: "", subtypes: deliveryOptions.map((d) => ({ key: d, label: deliveryLabels[d] || d.replace(/_/g, " ") })) }]
              ).map((grp) => (
                <div key={grp.key} data-testid={`delivery-group-${grp.key}`}>
                  {grp.label && (
                    <div className="text-[11px] font-bold text-[#888] uppercase tracking-wider mb-1">
                      {grp.label}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2">
                    {grp.subtypes.map((s) => {
                      const on = form.delivery.has(s.key);
                      return (
                        <button
                          type="button"
                          key={s.key}
                          onClick={toggleSet("delivery", s.key)}
                          className={`tv-chip ${on ? "tv-chip-on" : ""}`}
                          data-testid={`input-delivery-${s.key}`}
                        >
                          {on ? "[x] " : "[ ] "}{s.label || s.key}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </Field>

          <label className="flex items-center gap-2 text-[13px] cursor-pointer select-none" data-testid="field-is-open-source">
            <input
              type="checkbox"
              checked={form.is_open_source}
              onChange={(e) => setForm((f) => ({ ...f, is_open_source: e.target.checked }))}
              data-testid="input-is-open-source"
              className="accent-black"
            />
            <span>Open source — source is public / self-hostable (GitHub, pip, docker)</span>
          </label>

          <Field label="Function *" testid="field-category">
            <select required className="tv-select" value={form.category} onChange={update("category")} data-testid="input-category">
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>

          <Field label="Secondary function" testid="field-category-2">
            <select className="tv-select" value={form.category_2} onChange={update("category_2")} data-testid="input-category-2">
              <option value="">— none —</option>
              {categories.filter((c) => c !== form.category).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>

          <Field label="Pricing *" testid="field-pricing">
            <input type="text" required maxLength={16} className="tv-input" value={form.pricing} onChange={update("pricing")} placeholder="$ or FREE-$$ etc." data-testid="input-pricing" list="pricing-suggestions" />
            <datalist id="pricing-suggestions">
              {PRICING_OPTIONS.map((p) => (
                <option key={p} value={p}>{p === "$?" ? "unknown" : (PRICING_LABELS[p] || "range")}</option>
              ))}
            </datalist>
          </Field>

          <Field label="Pricing model" testid="field-pricing-model">
            <select className="tv-select" value={form.pricing_model} onChange={update("pricing_model")} data-testid="input-pricing-model">
              <option value="">— unset —</option>
              {pricingModels.map((pm) => (
                <option key={pm} value={pm}>{pm.replace("_", " ")}</option>
              ))}
            </select>
          </Field>

          <Field label="Instruments" testid="field-instruments">
            <div className="flex flex-wrap gap-2" data-testid="input-instruments">
              {instruments.map((inst) => {
                const on = form.instruments.has(inst);
                return (
                  <button
                    type="button"
                    key={inst}
                    onClick={toggleSet("instruments", inst)}
                    className={`tv-chip ${on ? "tv-chip-on" : ""}`}
                    data-testid={`input-instrument-${inst.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {on ? "[x] " : "[ ] "}{inst}
                  </button>
                );
              })}
            </div>
          </Field>

          <Field label="Regions" testid="field-regions">
            <div className="flex flex-wrap gap-2" data-testid="input-regions">
              {regions.map((r) => {
                const on = form.regions.has(r);
                return (
                  <button
                    type="button"
                    key={r}
                    onClick={toggleSet("regions", r)}
                    className={`tv-chip ${on ? "tv-chip-on" : ""}`}
                    data-testid={`input-region-${r.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {on ? "[x] " : "[ ] "}{r}
                  </button>
                );
              })}
            </div>
          </Field>

          <Field label="Sector (GICS — only if the tool focuses one industry)" testid="field-sectors">
            <div className="flex flex-wrap gap-2" data-testid="input-sectors">
              {sectors.map((s) => {
                const on = form.sectors.has(s);
                return (
                  <button
                    type="button"
                    key={s}
                    onClick={toggleSet("sectors", s)}
                    className={`tv-chip ${on ? "tv-chip-on" : ""}`}
                    data-testid={`input-sector-${s.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {on ? "[x] " : "[ ] "}{s}
                  </button>
                );
              })}
            </div>
          </Field>

          <Field label="Audience" testid="field-audience">
            <select className="tv-select" value={form.audience} onChange={update("audience")} data-testid="input-audience">
              <option value="">— unset —</option>
              <option value="retail">retail — built for individual investors / traders</option>
              <option value="pro">pro — built for professionals / institutions</option>
            </select>
          </Field>

          {form.audience === "pro" && (
            <Field label="Pro segment" testid="field-pro-segment">
              <select className="tv-select" value={form.pro_segment} onChange={update("pro_segment")} data-testid="input-pro-segment">
                <option value="">— unset —</option>
                {proSegments.map((s) => (
                  <option key={s} value={s}>{s.replace("_", " ")}</option>
                ))}
              </select>
            </Field>
          )}

          <Field label="Builder" testid="field-builder">
            <select className="tv-select" value={form.builder} onChange={update("builder")} data-testid="input-builder">
              <option value="">— unset —</option>
              <option value="indie">indie — solo / hobby / no business behind it</option>
              <option value="company">company — incorporated business / team</option>
            </select>
          </Field>

          <Field label="One-sentence description *" testid="field-description" hint={`${form.description.length}/240`}>
            <textarea required maxLength={240} rows={3} className="tv-textarea" value={form.description} onChange={update("description")} placeholder="What does it do, in one sentence." data-testid="input-description" />
          </Field>

          <Field label="Key feature *" testid="field-keyfeature">
            <input type="text" required maxLength={120} className="tv-input" value={form.key_feature} onChange={update("key_feature")} placeholder="e.g. Real-time SEC filing diffs with citations" data-testid="input-key-feature" />
          </Field>

          <Field label="Your name" testid="field-name">
            <input type="text" maxLength={120} className="tv-input" value={form.submitter_name} onChange={update("submitter_name")} placeholder="e.g. Jane Doe" data-testid="input-name" />
          </Field>

          <Field label="Your email" testid="field-email">
            <input type="email" maxLength={200} className="tv-input" value={form.submitter_email} onChange={update("submitter_email")} placeholder="you@domain.com" data-testid="input-email" />
          </Field>

          <label className="flex items-center gap-2 text-[13px] cursor-pointer select-none" data-testid="field-is-owner">
            <input
              type="checkbox"
              checked={form.is_owner}
              onChange={(e) => setForm((f) => ({ ...f, is_owner: e.target.checked }))}
              data-testid="input-is-owner"
              className="accent-black"
            />
            <span>Is this your company or a tool you built?</span>
          </label>
          {form.is_owner && vendorFlags.claiming && (
            <p className="text-[11px] text-[#666] -mt-2" data-testid="submit-claim-hint">
              once approved you can claim the listing (free) with your work email — <a href="/for-vendors" className="tv-link">how it works</a>
            </p>
          )}

          <div className="flex items-center gap-2 mt-2">
            <button type="submit" className="tv-btn tv-btn-primary" disabled={submitting} data-testid="submit-tool-btn">
              {submitting ? "[ submitting... ]" : "[+] Submit for review"}
            </button>
            <button type="button" className="tv-btn" onClick={() => nav("/")} data-testid="cancel-submit-btn">
              [esc] cancel
            </button>
          </div>
        </form>
      </main>
      <Footer />
    </div>
  );
}

function Field({ label, hint, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <div className="flex items-baseline justify-between">
        <span className="text-[12px] font-bold">{label}</span>
        {hint && <span className="text-[11px] text-[#888]">{hint}</span>}
      </div>
      <div className="mt-1">{children}</div>
    </label>
  );
}
